import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(1.5),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  meta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  fields: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  value: {
    fontSize: "0.85rem",
    overflowWrap: "anywhere",
    marginInlineStart: 0,
  },
}));

/**
 * One row of a list, rendered as a stacked card. Used below `md`, where a wide
 * table is unreadable on a phone or a folded foldable.
 *
 * `header` is the always-visible top line (checkbox, link, status, total).
 * `meta` is an optional chip row. Children should be <ListCardField/>.
 */
const ListCard = ({ header, meta, children, className, ...rest }) => {
  const classes = useStyles();

  return (
    <Paper variant="outlined" className={clsx(classes.card, className)} {...rest}>
      {header ? <div className={classes.header}>{header}</div> : null}
      {meta ? <div className={classes.meta}>{meta}</div> : null}
      {children ? <dl className={classes.fields}>{children}</dl> : null}
    </Paper>
  );
};

ListCard.propTypes = {
  header: PropTypes.node,
  meta: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

/** A label/value pair inside a <ListCard/>. Renders an em dash when empty. */
export const ListCardField = ({ label, value }) => {
  const classes = useStyles();

  return (
    <>
      <Typography component="dt" className={classes.label}>
        {label}
      </Typography>
      <Typography component="dd" className={classes.value}>
        {value === 0 ? value : value || "—"}
      </Typography>
    </>
  );
};

ListCardField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
};

export default ListCard;
